import {
	getPopupDetails,
	getPopupDetailsResolved,
	getPopupDetailsRejected,
	getPopupInspectionsResolved,
	getPopupInspectionsRejected,
	getPopupInspections,
	getPopupAssetResolved,
	getPopupAsset,
	getPopupInspectionsClear,
	getPopupRiskResolved,
	getPopupRiskClear,
	getPopupRiskRejected,
	getPopupRisk,
} from '@Actions/popup.actions';
import { put, retry, select, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { getInspectionApi, getRiskApi, getSummaryApi } from '@Services';
import { PopupDetailsResponse } from '@Types/popup.types';
import { popupSelectedAssetSelector } from '@Selectors/popup.selectors';
import { PayloadAction } from '@reduxjs/toolkit';
import {
	AssetDetailParams,
	AssetRiskAndCondition,
} from '@innovyze/lib-am-common/types/asset.types';
import { InspectionResolvedResponse } from '@innovyze/lib-am-common/types/inspectionAttributes.types';
import { assetSchemaSelectorByType } from '@innovyze/lib_asset_schema_store';
import { DefaultRootState } from 'react-redux';

function* getPopupDetailsSaga(action: PayloadAction<AssetDetailParams>) {
	const sameAsset: ReturnType<typeof popupSelectedAssetSelector> =
		yield select(popupSelectedAssetSelector);
	// don't re-request the information if we already have it
	if (sameAsset) return;
	const { assetType } = action.payload;
	const assetSchema: ReturnType<typeof assetSchemaSelectorByType> =
		yield select((state: DefaultRootState) =>
			assetSchemaSelectorByType(state, assetType),
		);

	yield put(getPopupAsset(action.payload));
	if (assetSchema?.hasCapability('Inspections')) {
		yield put(getPopupInspections(action.payload.assetId));
	}
	if (assetSchema?.hasCapability('RiskScore')) {
		yield put(getPopupRisk(action.payload));
	}

	yield put(getPopupDetailsResolved());
}
function* getPopupAssetSaga(action: PayloadAction<AssetDetailParams>) {
	try {
		const payload: AxiosResponse<PopupDetailsResponse> = yield retry(
			5,
			1500,
			getSummaryApi,
			action.payload,
		);
		yield put(
			getPopupAssetResolved({
				assetId: action.payload.assetId,
				assetType: action.payload.assetType,
				...payload.data.asset,
			}),
		);
	} catch (e) {
		yield put(getPopupDetailsRejected());
	}
}

function* getPopupInspectionSaga(action: PayloadAction<string>) {
	try {
		yield put(getPopupInspectionsClear());
		const payload: AxiosResponse<InspectionResolvedResponse> = yield retry(
			5,
			1500,
			getInspectionApi,
			action.payload,
		);
		// See comments in package.md wrt eslint-disable-line below
		yield put(getPopupInspectionsResolved(payload.data?.data)); // eslint-disable-line
	} catch (e) {
		yield put(getPopupInspectionsRejected());
	}
}

function* getPopupRiskSaga(action: PayloadAction<AssetDetailParams>) {
	try {
		yield put(getPopupRiskClear());
		const payload: AxiosResponse<AssetRiskAndCondition[]> = yield retry(
			5,
			1500,
			getRiskApi,
			action.payload,
		);
		// See comments in package.md wrt eslint-disable-line below
		yield put(getPopupRiskResolved(payload.data?.[0])); // eslint-disable-line
	} catch (e) {
		yield put(getPopupRiskRejected());
	}
}

function* watchGetPopupDetails() {
	yield takeLatest(getPopupDetails, getPopupDetailsSaga);
}

function* watchGetPopupAsset() {
	yield takeLatest(getPopupAsset, getPopupAssetSaga);
}

function* watchGetPopupInspections() {
	yield takeLatest(getPopupInspections, getPopupInspectionSaga);
}

function* watchGetPopupRisk() {
	yield takeLatest(getPopupRisk, getPopupRiskSaga);
}

export default [
	watchGetPopupDetails(),
	watchGetPopupAsset(),
	watchGetPopupInspections(),
	watchGetPopupRisk(),
];
